<template>
  <div :class="containerClass">
    <page-header
      title="Configure Basic Website Settings"
      :container-class="null"
      class="mb-16pt"
      :info-text="
        getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL
          ? `Fine-tune your website's basic features for optimal performance.`
          : ''
      "
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group
        :label="$t('schoolMsgs.school')"
        label-for="school"
        label-class="form-label"
        v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN"
      >
        <v-select
          id="school"
          class="form-control v-select-custom"
          label="name"
          v-model="school"
          :reduce="(school) => school.id"
          placeholder="Select School"
          :options="allSchools"
          :loading="areSchoolsLoading"
          @input="prefillLogo(school)"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!school" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.name }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.school.required && $v.school.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>
      <div v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN ? this.school : true">
        <template v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN">
          <page-separator title="Website Theme" />

          <b-form-group label="Theme" label-for="type" label-class="form-label">
            <v-select
              id="type"
              class="form-control v-select-custom"
              label="text"
              v-model="basicConfig.theme"
              :reduce="(type) => type.value"
              placeholder="Select theme"
              :options="typeOptions"
            >
              <template #search="{ attributes, events }">
                <input class="vs__search" v-bind="attributes" v-on="events" />
              </template>
            </v-select>
          </b-form-group>
        </template>

        <page-separator title="Website Scheme" />
        <b-form-group label="Font (Header + Body)" label-for="font" label-class="form-label">
          <v-select
            id="font"
            class="form-control v-select-custom"
            label="text"
            v-model="font"
            :reduce="(font) => font.value"
            placeholder="Select Header And Body Font"
            :options="allFonts"
            :clearable="false"
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" v-bind="attributes" v-on="events" />
            </template>

            <template slot="option" slot-scope="option">
              <div class="d-flex align-items-center">
                <span style="font-size: 14px">{{ option.text }}</span>
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="d-flex align-items-center">
                <span style="font-size: 14px">{{ option.text }}</span>
              </div>
            </template>
          </v-select>
        </b-form-group>

        <b-form-group label="Color Scheme" label-for="type" label-class="form-label">
          <v-select
            id="type"
            class="form-control v-select-custom"
            label="text"
            v-model="basicConfig.color"
            :reduce="(type) => type.value"
            placeholder="Select color scheme"
            :options="colorTypeOptions"
            :clearable="false"
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" v-bind="attributes" v-on="events" />
            </template>
          </v-select>
        </b-form-group>

        <page-separator title="Website Navbar" />

        <b-form-group label="Header / Navbar Style" label-class="form-label">
          <div class="row">
            <div class="col-md-4 col-6" v-for="variant in headerVariants" :key="`header-${variant}`">
              <div class="layout-thumb text-center clickable-item mb-3" @click.prevent="basicConfig.headerVariant = variant">
                <figure class="mb-1">
                  <img
                    width="100%"
                    :src="getHeaderVariantImg(variant)"
                    :alt="headerVariantLabels[variant] || variant"
                    :class="basicConfig.headerVariant === variant ? 'border-outline-primary' : ''"
                    :style="basicConfig.headerVariant === variant
                      ? 'object-fit: cover; object-position: top;'
                      : 'border: #f2f2f2 2px solid; border-radius: 8px; object-fit: cover; object-position: top;'"
                  />
                </figure>
                <small :class="basicConfig.headerVariant === variant ? 'text-primary font-weight-bold' : 'text-muted'">
                  {{ headerVariantLabels[variant] || variant }}
                </small>
              </div>
            </div>
          </div>
          <small class="text-muted d-block">
            Choose how the navbar looks across every page of your site. The Enroll button, phone, and menu links stay
            the same &mdash; only the layout changes.
          </small>
        </b-form-group>

        <b-form-group label="Logo" label-for="image" label-class="form-label" class="row-align-items-center">
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isImgUploading">...</span>
              <b-img
                :src="basicConfig.logo_url"
                class="img-fluid"
                width="40"
                alt="Image"
                v-else-if="basicConfig.logo_url"
              />
              <i class="fas fa-university fa-lg" v-else></i>
            </fmv-avatar>

            <image-uploader
              @image="(e) => setImage(e, 'image')"
              :isUploading="isImgUploading"
              :isRequired="false"
              placeholder="Select Logo"
            />
          </b-media>
        </b-form-group>

        <b-form-group label="Navbar Menu" label-class="form-label">
          <draggable class="draggable-cursor" @start="dragging = true" @end="updateOrder" :list="basicConfig.navMenu">
            <b-form-group
              v-for="(menu, index) in basicConfig.navMenu"
              :key="`${menu}-${index}`"
              label-class="form-label"
            >
              <!-- Dynamic submenu handling - works for any menu item with sub_menus -->
              <div v-if="menu.sub_menus && menu.sub_menus.length" class="card card-lg overlay--primary-dodger-blue mb-1">
                <div class="card-body d-flex flex-column">
                  <div class="d-flex align-items-center">
                    <div class="col-8 col-md-8 my-2 d-flex">
                      <i class="fas fa-bars icon-16pt mr-3"></i>

                      <editable-label
                        :inputLabel="menu.title"
                        v-model="menu.title"
                        @saveLabel="changeTitle(basicConfig.navMenu)"
                      />
                    </div>
                    <div class="col-4 col-md-4 d-flex justify-content-end">
                      <b-form-checkbox class="ml-auto" v-model="menu.is_enable" name="check-button" switch>
                        Enabled?
                      </b-form-checkbox>
                    </div>
                  </div>
                  <!-- Display all submenus dynamically. Program custom pages are reorderable via drag handle. -->
                  <draggable
                    :list="menu.sub_menus"
                    :disabled="menu.name !== 'program'"
                    handle=".submenu-drag-handle"
                    @end="updateSubMenuOrder"
                  >
                    <div
                      v-for="(sub_menu, subMenuIndex) in menu.sub_menus"
                      :key="`${sub_menu}-${subMenuIndex}`"
                      class="card-body d-flex flex-column mt-2"
                    >
                      <div class="d-flex align-items-center">
                        <div class="col-6 col-md-6 ml-4 d-flex align-items-center">
                          <i
                            v-if="menu.name === 'program'"
                            class="fas fa-bars icon-16pt mr-3 submenu-drag-handle draggable-cursor"
                          ></i>
                          <editable-label
                            :inputLabel="sub_menu.title"
                            v-model="sub_menu.title"
                            @saveLabel="changeTitle(basicConfig.navMenu)"
                          />
                        </div>
                        <div class="col-4 col-md-4 d-flex justify-content-end">
                          <b-form-checkbox class="ml-auto" v-model="sub_menu.is_enable" name="check-button" switch>
                            Enabled?
                          </b-form-checkbox>
                        </div>
                      </div>
                    </div>
                  </draggable>
                </div>
              </div>

              <!-- Regular menu item without submenus -->
              <div v-else class="card card-lg overlay--primary-dodger-blue mb-1">
                <div class="card-body d-flex flex-column">
                  <div class="d-flex align-items-center">
                    <div class="col-8 col-md-8 d-flex">
                      <i class="fas fa-bars icon-16pt mr-3"></i>

                      <editable-label
                        :inputLabel="menu.title"
                        v-model="menu.title"
                        @saveLabel="changeTitle(basicConfig.navMenu)"
                      />
                    </div>
                    <div class="col-4 col-md-4 d-flex justify-content-end">
                      <b-form-checkbox class="ml-auto" v-model="menu.is_enable" name="check-button" switch>
                        Enabled?
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>
              </div>
            </b-form-group>
          </draggable>
        </b-form-group>

        <page-separator title="Website Top Bar" />

        <div class="row">
          <b-form-group
            v-for="(top, index) in basicConfig.topBar"
            :key="`${top}-${index}`"
            label-class="form-label"
            class="col-6"
          >
            <b-form-checkbox v-model="top.is_enable" name="check-button" switch>
              {{ $t(`topbarFields.${top.name}`) }}
            </b-form-checkbox>
          </b-form-group>
        </div>

        <page-separator>
          Website Placeholders
          <md-icon
            style="font-size: 20px; color: grey"
            class="svg-icon mr-2"
            v-b-popover.hover.right
            title="Will be used in various places like classes section, etc."
            >info</md-icon
          >
        </page-separator>

        <b-form-group
          label="Default Image"
          label-for="default-image"
          label-class="form-label"
          class="row-align-items-center"
        >
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isDefaultImgUploading">...</span>
              <b-img
                :src="basicConfig.defaultImageUrl"
                class="img-fluid"
                width="40"
                alt="Image"
                v-else-if="basicConfig.defaultImageUrl"
              />
              <i class="fas fa-university fa-lg" v-else></i>
            </fmv-avatar>

            <image-uploader
              @image="(e) => setImage(e, 'default_image')"
              :isUploading="isDefaultImgUploading"
              :isRequired="false"
              placeholder="Select Image"
            />
          </b-media>
        </b-form-group>

        <b-form-group label="Default Video URL" label-for="default-video-url" label-class="form-label">
          <b-form-input
            id="default-video-url"
            placeholder="Enter default video URL"
            v-model="basicConfig.defaultVideoUrl"
          />
        </b-form-group>

        <page-separator title="Website Footer" />

        <b-form-group label="Footer Style" label-class="form-label">
          <div class="row">
            <div class="col-md-4 col-6" v-for="variant in footerVariants" :key="`footer-${variant}`">
              <div class="layout-thumb text-center clickable-item mb-3" @click.prevent="basicConfig.footerVariant = variant">
                <figure class="mb-1">
                  <img
                    width="100%"
                    :src="getFooterVariantImg(variant)"
                    :alt="footerVariantLabels[variant] || variant"
                    :class="basicConfig.footerVariant === variant ? 'border-outline-primary' : ''"
                    :style="basicConfig.footerVariant === variant
                      ? 'object-fit: cover; object-position: top;'
                      : 'border: #f2f2f2 2px solid; border-radius: 8px; object-fit: cover; object-position: top;'"
                  />
                </figure>
                <small :class="basicConfig.footerVariant === variant ? 'text-primary font-weight-bold' : 'text-muted'">
                  {{ footerVariantLabels[variant] || variant }}
                </small>
              </div>
            </div>
          </div>
          <small class="text-muted d-block">
            Choose how the footer looks across every page of your site. Your logo, contact info, and social links stay
            the same &mdash; only the layout changes.
          </small>
        </b-form-group>

        <page-separator title="Buttons" />

        <b-form-group label="Button Style" label-class="form-label">
          <div class="row">
            <div class="col-md-4 col-6" v-for="style in buttonStyles" :key="`button-${style}`">
              <div class="layout-thumb text-center clickable-item mb-3" @click.prevent="basicConfig.buttonStyle = style">
                <figure class="mb-1">
                  <img
                    width="100%"
                    :src="getButtonStyleImg(style)"
                    :alt="buttonStyleLabels[style] || style"
                    :class="basicConfig.buttonStyle === style ? 'border-outline-primary' : ''"
                    :style="basicConfig.buttonStyle === style
                      ? 'object-fit: cover; object-position: top;'
                      : 'border: #f2f2f2 2px solid; border-radius: 8px; object-fit: cover; object-position: top;'"
                  />
                </figure>
                <small :class="basicConfig.buttonStyle === style ? 'text-primary font-weight-bold' : 'text-muted'">
                  {{ buttonStyleLabels[style] || style }}
                </small>
              </div>
            </div>
          </div>
          <small class="text-muted d-block">
            Choose the corner shape of buttons across your whole site &mdash; Enroll, Apply, and every other
            call-to-action. Colors and text stay the same.
          </small>
        </b-form-group>

        <b-form-group label="Logo" label-for="image" label-class="form-label" class="row-align-items-center">
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isFooterImgLoading">...</span>
              <b-img
                :src="basicConfig.footer_logo_url"
                class="img-fluid"
                width="40"
                alt="Image"
                v-else-if="basicConfig.footer_logo_url"
              />
              <i class="fas fa-university fa-lg" v-else></i>
            </fmv-avatar>

            <image-uploader
              @image="(e) => setImage(e, 'footer_image')"
              :isUploading="isFooterImgLoading"
              :isRequired="false"
              placeholder="Select Footer Logo"
            />
          </b-media>
        </b-form-group>

        <div class="row">
          <b-form-group
            v-for="(item, index) in basicConfig.footer"
            :key="`${index}-${item}`"
            label-class="form-label"
            class="col-6"
          >
            <b-form-checkbox v-model="item.is_enable" name="check-button" switch
              >{{ $t(`footer.${item.name}`) }}
            </b-form-checkbox>
          </b-form-group>
        </div>

        <b-form-group label="Footer Description" label-for="footer_desc" label-class="form-label">
          <b-form-textarea
            id="footer_desc"
            :rows="5"
            placeholder="Enter footer description (if any)"
            v-model.trim="basicConfig.footer_desc"
          />
        </b-form-group>

        <b-form-group label="Social Links" label-for="name" label-class="form-label">
          <div class="row ml-1 mb-2">
            <b-icon class="mr-3 mt-2 text-primary" scale="1.8" icon="facebook" aria-hidden="true"></b-icon>
            <fmv-input-group-merge
              class="col-11 no-padding"
              id="name"
              placeholder=""
              v-model="basicConfig.footerFacebook"
              prepend
            >
              {{ basicConfig.facebookUrl }}
            </fmv-input-group-merge>
          </div>

          <div class="row ml-1 mb-2 text-primary">
            <b-icon class="mr-3 mt-2" scale="1.8" icon="instagram" aria-hidden="true"></b-icon>
            <fmv-input-group-merge
              class="col-11 no-padding"
              id="name"
              placeholder=""
              v-model="basicConfig.footerInstagram"
              prepend
            >
              {{ basicConfig.instagramUrl }}</fmv-input-group-merge
            >
          </div>

          <div class="row ml-1 mb-2 text-primary">
            <b-icon class="mr-3 mt-2" scale="1.8" icon="twitter" aria-hidden="true"></b-icon>
            <fmv-input-group-merge
              class="col-11 no-padding"
              id="name"
              placeholder=""
              v-model="basicConfig.footerTwitter"
              prepend
            >
              {{ basicConfig.twitterUrl }}
            </fmv-input-group-merge>
          </div>

          <div class="row ml-1 mb-2 text-primary">
            <b-icon class="mr-3 mt-2" scale="1.8" icon="youtube" aria-hidden="true"></b-icon>
            <fmv-input-group-merge
              class="col-11 no-padding"
              id="name"
              placeholder=""
              v-model="basicConfig.footerYoutube"
              prepend
            >
              {{ basicConfig.youtubeUrl }}
            </fmv-input-group-merge>
          </div>

          <div class="row ml-1 mb-2 text-primary">
            <b-icon class="mr-3 mt-2" scale="1.8" icon="linkedin" aria-hidden="true"></b-icon>
            <fmv-input-group-merge
              class="col-11 no-padding"
              id="name"
              placeholder=""
              v-model="basicConfig.footerLinkedin"
              prepend
            >
              {{ basicConfig.linkedinUrl }}
            </fmv-input-group-merge>
          </div>

          <div class="row ml-1 mb-2 text-primary">
            <i class="fab fa-tiktok mr-3 mt-2" style="font-size: 1.3rem" aria-hidden="true"></i>
            <fmv-input-group-merge
              class="col-11 no-padding"
              id="name"
              placeholder=""
              v-model="basicConfig.footerTiktok"
              prepend
            >
              {{ basicConfig.tiktokUrl }}
            </fmv-input-group-merge>
          </div>
        </b-form-group>

        <page-separator>
          Meta Titles
          <md-icon
            style="font-size: 20px; color: grey"
            class="svg-icon mr-2"
            v-b-popover.hover.right
            title="Will be used for SEO purposes"
            >info</md-icon
          >
        </page-separator>
        <p class="text-70">
          Configure meta title for each custom page in
          <a style="color:deepskyblue;" href="custom-pages/">custom page management</a> section
        </p>

        <b-form-group label="Home" label-for="home-title" label-class="form-label">
          <b-form-textarea
            id="home-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.homeTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="About" label-for="about-title" label-class="form-label">
          <b-form-textarea
            id="about-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.aboutTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Tuition" label-for="tuition-title" label-class="form-label">
          <b-form-textarea
            id="tuition-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.tuitionTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Locations" label-for="location-title" label-class="form-label">
          <b-form-textarea
            id="location-title"
            placeholder="Enter location"
            rows="3"
            maxlength="200"
            v-model="meta.locTitle"
          ></b-form-textarea>
        </b-form-group>
        <b-form-group label="Gallery" label-for="gallery-title" label-class="form-label">
          <b-form-textarea
            id="gallery-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.galleryTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="FAQ" label-for="faq-title" label-class="form-label">
          <b-form-textarea
            id="faq-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.faqTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Resource" label-for="res-title" label-class="form-label">
          <b-form-textarea
            id="res-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.resTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Blog" label-for="blog-title" label-class="form-label">
          <b-form-textarea
            id="blog-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.blogTitle"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Contact" label-for="contact-title" label-class="form-label">
          <b-form-textarea
            id="contact-title"
            placeholder="Enter title"
            rows="3"
            maxlength="200"
            v-model="meta.contactTitle"
          ></b-form-textarea>
        </b-form-group>

        <page-separator>
          Meta Descriptions
          <md-icon
            style="font-size: 20px; color: grey"
            class="svg-icon mr-2"
            v-b-popover.hover.right
            title="Will be used for SEO purposes"
            >info</md-icon
          >
        </page-separator>
        <p class="text-70">
          Configure meta description for each custom page in
          <a style="color:deepskyblue;" href="custom-pages/">custom page management</a> section
        </p>
        <b-form-group label="Home" label-for="home-desc" label-class="form-label">
          <b-form-textarea
            id="home-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.homeDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="About" label-for="about-desc" label-class="form-label">
          <b-form-textarea
            id="about-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.aboutDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Tuition" label-for="tuition-desc" label-class="form-label">
          <b-form-textarea
            id="tuition-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.tuitionDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Locations" label-for="location-desc" label-class="form-label">
          <b-form-textarea
            id="location-desc"
            placeholder="Enter location"
            rows="3"
            maxlength="200"
            v-model="meta.locDescription"
          ></b-form-textarea>
        </b-form-group>
        <b-form-group label="Gallery" label-for="gallery-desc" label-class="form-label">
          <b-form-textarea
            id="gallery-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.galleryDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="FAQ" label-for="faq-desc" label-class="form-label">
          <b-form-textarea
            id="faq-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.faqDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Resource" label-for="res-desc" label-class="form-label">
          <b-form-textarea
            id="res-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.resDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Blog" label-for="blog-desc" label-class="form-label">
          <b-form-textarea
            id="blog-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.blogDescription"
          ></b-form-textarea>
        </b-form-group>

        <b-form-group label="Contact" label-for="contact-desc" label-class="form-label">
          <b-form-textarea
            id="contact-desc"
            placeholder="Enter description"
            rows="3"
            maxlength="200"
            v-model="meta.contactDescription"
          ></b-form-textarea>
        </b-form-group>

        <page-separator title="Contact Page Style" />
        <section-style-panel
          v-model="contactPageStyle"
          :capabilities="PAGE_STYLE_CAPABILITIES.contact"
          :organization="selectedOrganization"
        />

        <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
          <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
          <span v-else>Update</span>
        </b-btn>
      </div>
    </b-form>
  </div>
</template>

<style lang="css" scoped>
@import './style.css';
</style>

<script>
import { SCHOOL_PAYMENT_PLANS, USER_ROLES, USER_ROLE_TYPES } from '@/common/constants';
import ImageUploader from '@/components/ImageUploader.vue';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import axios from 'axios';
import { FmvAvatar } from 'fmv-avatar';
import { FmvInputGroupMerge } from 'fmv-input-group-merge';
import { get, isEmpty, replace } from 'lodash';
import vSelect from 'vue-select';
import draggable from 'vuedraggable';
import { requiredIf } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import EditableLabel from './EditableLabel.vue';
import SectionStylePanel, { SECTION_STYLE_DEFAULTS } from '@/views/SuperAdminPortal/WebsiteConfig/SectionStylePanel.vue';
import { PAGE_STYLE_CAPABILITIES } from '@/components/WebsiteConfig/pageStyleMixin';
import { HEADER_VARIANTS, HEADER_VARIANT_LABELS, headerVariantImage, FOOTER_VARIANTS, FOOTER_VARIANT_LABELS, footerVariantImage, BUTTON_STYLES, BUTTON_STYLE_LABELS, buttonStyleImage } from '@/common/sectionLayouts';

export default {
  components: {
    PageHeader,
    draggable,
    FmvAvatar,
    vSelect,
    FmvInputGroupMerge,
    PageSeparator,
    ImageUploader,
    EditableLabel,
    SectionStylePanel,
  },
  extends: Page,
  data() {
    return {
      SCHOOL_PAYMENT_PLANS,
      title: 'Configure Basic Website Settings',
      isAdded: false,
      school: null,
      dragging: false,
      PAGE_STYLE_CAPABILITIES,
      contactPageStyle: { ...SECTION_STYLE_DEFAULTS },
      meta: {
        homeDescription: '',
        aboutDescription: '',
        tuitionDescription: '',
        locDescription: '',
        galleryDescription: '',
        faqDescription: '',
        resDescription: '',
        blogDescription: '',
        contactDescription: '',
        homeTitle: '',
        aboutTitle: '',
        tuitionTitle: '',
        locTitle: '',
        galleryTitle: '',
        faqTitle: '',
        resTitle: '',
        blogTitle: '',
        contactTitle: '',
      },
      basicConfig: {
        navMenu: [
          { name: 'homepage', is_enable: true },
          { name: 'program', is_enable: false },
          { name: 'location', is_enable: false },
          {
            name: 'about',
            is_enable: false,
            sub_menus: [
              { name: 'about_our_school', is_enable: false },
              { name: 'faqs', is_enable: false },
              { name: 'tuition', is_enable: false },
            ],
          },
          { name: 'gallery', is_enable: false },
          { name: 'contact', is_enable: false },
          { name: 'student_center', is_enable: true },
        ],
        topBar: [
          { name: 'phone', is_enable: true },
          { name: 'email', is_enable: true },
          { name: 'enroll_now', is_enable: true },
        ],
        footer: [
          { name: 'phone', is_enable: true },
          { name: 'email', is_enable: true },
          { name: 'address', is_enable: true },
          { name: 'website', is_enable: true },
        ],
        tuition_reg_req: false,
        logo_url: null,
        defaultImageUrl: null,
        defaultVideoUrl: null,
        footer_logo_url: null,
        footer_desc: null,
        theme: '',
        color: null,
        headerVariant: 'Default',
        footerVariant: 'Default',
        buttonStyle: 'Rounded',

        footerFacebook: null,
        footerYoutube: null,
        footerLinkedin: null,
        footerTwitter: null,
        footerInstagram: null,
        footerTiktok: null,

        facebookUrl: 'https://www.facebook.com/',
        instagramUrl: 'https://www.instagram.com/',
        twitterUrl: 'https://twitter.com/',
        linkedinUrl: 'https://www.linkedin.com/',
        youtubeUrl: 'https://www.youtube.com/',
        tiktokUrl: 'https://www.tiktok.com/',
      },
      areSchoolsLoading: false,
      allSchools: [],
      isLoading: false,
      isImgUploading: false,
      isDefaultImgUploading: false,
      isFooterImgLoading: false,
      typeOptions: [
        { value: 'agency', text: 'Agency' },
        { value: 'medical', text: 'Medical' },
      ],
      colorTypeOptions: [
        { value: 'light', text: 'Light' },
        { value: 'dark', text: 'Dark' },
      ],
      headerVariants: HEADER_VARIANTS,
      headerVariantLabels: HEADER_VARIANT_LABELS,
      footerVariants: FOOTER_VARIANTS,
      footerVariantLabels: FOOTER_VARIANT_LABELS,
      buttonStyles: BUTTON_STYLES,
      buttonStyleLabels: BUTTON_STYLE_LABELS,
      font: 'Inter+Roboto',
      allFonts: [
        { value: 'Inter+Roboto', text: 'Inter + Roboto' },
        { value: 'Roboto+Poppins', text: 'Roboto + Poppins' },
        { value: 'Arvo+Lato', text: 'Arvo + Lato' },
        { value: 'Merriweather+Mulish', text: 'Merriweather + Mulish' },
        { value: 'Work Sans+Open Sans', text: 'Work Sans + Open Sans' },
        { value: 'Oswald+Quattrocento', text: 'Oswald + Quattrocento' },
        { value: 'Ovo+Quattrocento', text: 'Ovo + Quattrocento' },
        { value: 'Ubuntu+Open Sans', text: 'Ubuntu + Open Sans' },
        { value: 'Slabo 13px+Lato', text: 'Slabo 13px + Lato' },
        { value: 'Rufina+PT Serif', text: 'Rufina + PT Serif' },
      ],
      uploadFile: { image: null, footer_image: null, default_image: null },
      uploadPercent: { image: 0, footer_image: 0, default_image: 0 },
      uploadCancelTokenSource: { image: null, footer_image: null, default_image: null },
      USER_ROLES,
      USER_ROLE_TYPES,
    };
  },
  validations() {
    return {
      school: {
        required: requiredIf(() => get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN),
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    ...mapGetters('payoutAcc', ['getPayoutAccountStatus']),
    isFormLoading() {
      return this.isLoading || this.isImgUploading || this.areSchoolsLoading || this.isFooterImgLoading;
    },
    selectedOrganization() {
      if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
        return (this.allSchools || []).find(sch => sch.id === this.school);
      }
      return get(this.getLoggedInUser, 'linked_entity');
    },
  },

  methods: {
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('school', ['getAllSchools', 'getSchool']),
    ...mapActions('webConfig', [
      'getBasicConfig',
      'createBasicConfig',
      'getLinkedImageUploadPresignedUrl',
      'updateNavbar',
    ]),
    get,
    isEmpty,
    getHeaderVariantImg: headerVariantImage,
    getFooterVariantImg: footerVariantImage,
    getButtonStyleImg: buttonStyleImage,

    setImage(file, image) {
      this.uploadFile[image] = file;
      this.uploadImage(image);
    },
    async changeTitle(navMenu) {
      try {
        const data = {
          navbar_attribs: navMenu,
          ...(get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN && { school: this.school }),
        };
        await this.updateNavbar(data);
        this.makeToast({ variant: 'success', msg: 'Navbar Menu Title Updated!' });
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async updateSubMenuOrder() {
      // Persist the new order of program custom pages (program navbar sub_menus).
      // Order is stored purely as array position in navbar_attribs, so sending
      // the mutated navMenu is enough for both the editor and the public site.
      try {
        const data = {
          navbar_attribs: this.basicConfig.navMenu,
          ...(get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN && { school: this.school }),
        };
        await this.updateNavbar(data);
        this.makeToast({ variant: 'success', msg: 'Program pages reordered!' });
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async updateOrder() {
      // this.dragging = false;
      // const data = {
      //   custom_navbar_menu: map(this.items, (id, order_num) => ({
      //     id: id.id,
      //     order_num: order_num + 1,
      //   })),
      // };
      // try {
      //   await this.updateCustomNavbarItemOrder(data);
      // } catch (err) {
      //   this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      // }
    },
    resetData() {
      this.basicConfig.theme = null;
      this.basicConfig.color = null;

      this.basicConfig.footer_desc = null;
      this.basicConfig.logo_url = null;
      this.basicConfig.headerVariant = 'Default';
      this.basicConfig.footerVariant = 'Default';
      this.basicConfig.buttonStyle = 'Rounded';
      this.basicConfig.defaultImageUrl = null;
      this.basicConfig.defaultVideoUrl = null;
      this.basicConfig.navMenu = null;
      this.basicConfig.footer = null;
      this.basicConfig.footerTwitter = null;
      this.basicConfig.footerInstagram = null;
      this.basicConfig.footerYoutube = null;
      this.basicConfig.footerLinkedin = null;
      this.basicConfig.footerFacebook = null;
      this.basicConfig.footerTiktok = null;
      this.basicConfig.topBar = null;
      this.meta.homeDescription = '';
      this.meta.aboutDescription = '';
      this.meta.galleryDescription = '';
      this.meta.faqDescription = '';
      this.meta.tuitionDescription = '';

      // this.basicConfig.school = null;
    },
    prefillLogo(schoolId) {
      this.resetData();
      this.fetchBasicConfig(schoolId);
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({
        sch_payment_plan__title: `${this.SCHOOL_PAYMENT_PLANS.ADVANCED},${this.SCHOOL_PAYMENT_PLANS.ADVANCED_AND_CHAT}`,
      });
      this.allSchools = response.data;

      this.areSchoolsLoading = false;
    },
    async fetchBasicConfig(school_id = null) {
      let resp;
      if (school_id) {
        resp = await this.getBasicConfig({
          school_id: school_id,
        });
      } else {
        resp = await this.getBasicConfig({});
      }

      this.basicConfig.theme = resp.wbst_theme;
      this.basicConfig.color = resp.wbst_scheme;
      this.font = resp.font_option;
      this.basicConfig.logo_url = resp.navbar_logo;
      this.basicConfig.headerVariant = resp.header_variant || 'Default';
      this.basicConfig.footerVariant = resp.footer_variant || 'Default';
      this.basicConfig.buttonStyle = resp.button_style || 'Rounded';
      this.basicConfig.defaultImageUrl = resp.default_img_url;
      this.basicConfig.defaultVideoUrl = resp.default_video_url;
      this.basicConfig.footer_logo_url = resp.footer_logo;
      this.basicConfig.navMenu = resp.navbar_attribs;
      this.basicConfig.footer = resp.footer_contact_attribs;
      this.basicConfig.footer_desc = resp.footer_desc;

      this.basicConfig.footerTwitter = replace(resp.twitter_url, this.basicConfig.twitterUrl, '');
      this.basicConfig.footerInstagram = replace(resp.insta_url, this.basicConfig.instagramUrl, '');
      this.basicConfig.footerYoutube = replace(resp.youtube_url, this.basicConfig.youtubeUrl, '');
      this.basicConfig.footerLinkedin = replace(resp.linkedin_url, this.basicConfig.linkedinUrl, '');
      this.basicConfig.footerFacebook = replace(resp.fb_url, this.basicConfig.facebookUrl, '');
      this.basicConfig.footerTiktok = replace(resp.tiktok_url, this.basicConfig.tiktokUrl, '');

      this.basicConfig.topBar = resp.topbar_fields;

      this.meta.homeDescription = resp.hmpg_meta_desc;
      this.meta.aboutDescription = resp.abtpg_meta_desc;
      this.meta.locDescription = resp.location_meta_desc;
      this.meta.galleryDescription = resp.photos_meta_desc;
      this.meta.faqDescription = resp.faqs_meta_desc;
      this.meta.tuitionDescription = resp.tuition_meta_desc;
      this.meta.resDescription = resp.resources_meta_desc;
      this.meta.blogDescription = resp.blogs_meta_desc;
      this.meta.contactDescription = resp.contact_meta_desc;

      this.meta.homeTitle = resp.hmpg_meta_title;
      this.meta.aboutTitle = resp.abtpg_meta_title;
      this.meta.locTitle = resp.location_meta_title;
      this.meta.galleryTitle = resp.photos_meta_title;
      this.meta.faqTitle = resp.faqs_meta_title;
      this.meta.tuitionTitle = resp.tuition_meta_title;
      this.meta.resTitle = resp.resources_meta_title;
      this.meta.blogTitle = resp.blogs_meta_title;
      this.meta.contactTitle = resp.contact_meta_title;

      this.contactPageStyle = { ...SECTION_STYLE_DEFAULTS, ...(resp.contact_page_style || {}) };
    },
    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isImgUploading = uploadType === 'image';
      this.isDefaultImgUploading = uploadType === 'default_image';
      this.isFooterImgLoading = uploadType === 'footer_image';

      try {
        const urlResp = await this.getLinkedImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
          upload_type: 'images',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function (progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'image') {
          this.basicConfig.logo_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'footer_image') {
          this.basicConfig.footer_logo_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'default_image') {
          this.basicConfig.defaultImageUrl = urlResp.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'image') this.isImgUploading = false;
      if (uploadType === 'default_image') this.isDefaultImgUploading = false;
      if (uploadType === 'footer_image') this.isFooterImgLoading = false;
    },

    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const data = {
            wbst_theme: this.basicConfig.theme,
            wbst_scheme: this.basicConfig.color,

            navbar_logo: this.basicConfig.logo_url,
            header_variant: this.basicConfig.headerVariant,
            footer_variant: this.basicConfig.footerVariant,
            button_style: this.basicConfig.buttonStyle,
            default_img_url: this.basicConfig.defaultImageUrl,
            default_video_url: this.basicConfig.defaultVideoUrl || null,
            navbar_attribs: this.basicConfig.navMenu,
            footer_logo: this.basicConfig.footer_logo_url,
            footer_contact_attribs: this.basicConfig.footer,
            footer_desc: this.basicConfig.footer_desc,
            twitter_url: this.basicConfig.footerTwitter
              ? this.basicConfig.twitterUrl + this.basicConfig.footerTwitter
              : null,
            insta_url: this.basicConfig.footerInstagram
              ? this.basicConfig.instagramUrl + this.basicConfig.footerInstagram
              : null,
            youtube_url: this.basicConfig.footerYoutube
              ? this.basicConfig.youtubeUrl + this.basicConfig.footerYoutube
              : null,
            linkedin_url: this.basicConfig.footerLinkedin
              ? this.basicConfig.linkedinUrl + this.basicConfig.footerLinkedin
              : null,
            fb_url: this.basicConfig.footerFacebook
              ? this.basicConfig.facebookUrl + this.basicConfig.footerFacebook
              : null,
            tiktok_url: this.basicConfig.footerTiktok
              ? this.basicConfig.tiktokUrl + this.basicConfig.footerTiktok
              : null,
            topbar_fields: this.basicConfig.topBar,

            hmpg_meta_desc: this.meta.homeDescription,
            abtpg_meta_desc: this.meta.aboutDescription,
            location_meta_desc: this.meta.locDescription,
            photos_meta_desc: this.meta.galleryDescription,
            faqs_meta_desc: this.meta.faqDescription,
            tuition_meta_desc: this.meta.tuitionDescription,
            resources_meta_desc: this.meta.resDescription,
            blogs_meta_desc: this.meta.blogDescription,
            contact_meta_desc: this.meta.contactDescription,

            hmpg_meta_title: this.meta.homeTitle,
            abtpg_meta_title: this.meta.aboutTitle,
            location_meta_title: this.meta.locTitle,
            photos_meta_title: this.meta.galleryTitle,
            faqs_meta_title: this.meta.faqTitle,
            tuition_meta_title: this.meta.tuitionTitle,
            resources_meta_title: this.meta.resTitle,
            blogs_meta_title: this.meta.blogTitle,
            contact_meta_title: this.meta.contactTitle,

            contact_page_style: this.contactPageStyle,

            font_option: this.font,
            school:
              get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
                ? this.school
                : this.getLoggedInUser.linked_entity.id,
          };

          await this.createBasicConfig({
            ...data,
          });
          this.makeToast({ variant: 'success', msg: 'Settings Configured!' });
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.isLoading = true;

    if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
      this.fetchSchools();
    } else if (
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
    ) {
      (await this.getSchool(this.getLoggedInUser.linked_entity.id)).data;
    }

    try {
      if (
        get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
        get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
      ) {
        this.fetchBasicConfig();
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
    }

    this.isLoading = false;
  },
};
</script>
